import React, { forwardRef, useState } from 'react'
import styled from 'styled-components'
import PropTypes from 'prop-types'
import { axios } from 'api'
import { useUserContext } from 'context'
import { Box, Flex, Image, NameList, P, ResponsiveSVG } from 'ui/acro'
import ClassCountBadge from './ClassCountBadge'
import { CardSubtitle, CardTitle } from '../styles'
import ThreeDots from '../../../../images/latest/three-dots.svg'
import heartSVG from '../../../../../assets/images/heart.svg'
import heartFilledSVG from '../../../../../assets/images/heartFilled.svg'
import { storeEventInLocalStorage } from 'gtm/pushEvents'
import { AlomovesIconSVG } from 'images/latest'
import SubCategoryChipList from './SubCategoryChipList'
import CoachesListComponent from './CoachesListComponent'
import AccessIdentifier from './AccessIdentifier'
import { showLabel, getLocalDate } from './utils'
import { camelToSnake } from 'utils'
import PopoverMenu from 'components_v2/MyPractice/common/Components/PopoverMenu'
import CommonLink from 'components_v2/CommonLink'
import { useInRouterContext } from 'react-router-dom'

const HeartIconBox = styled(Box)`
  svg {
    path {
      &.heart-icon {
        fill: #3b3b3b99;
      }
    }
  }
`

const Main = styled(Flex)`
  .no-image {
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #f2f2f2;
    aspect-ratio: 16/9;
  }
  .series-image {
    width: 100%;
  }
  .completed-at {
    margin-top: 4px;
    font-size: 14px;
    color: #758e6d;
  }
`

const TitleRow = styled(Flex)`
  justify-content: space-between;
`

const SeriesCard = forwardRef(
  (
    {
      carouselItem,
      id,
      classCount,
      workoutCount,
      instructors,
      primaryCategory,
      primarySubcategory,
      thumbnails,
      title,
      slug,
      url,
      introVideoThumbnail,
      thumbnailImage,
      coaches,
      alignBox,
      variantTitle,
      variantSubtitle,
      favoriteCallback,
      subcategory,
      coverPhoto,
      position,
      eventName,
      location,
      difficultyLevel,
      isFevIcon = true,
      subcategories,
      availabilityGating,
      membershipTier,
      isGridView,
      hideSave = false,
      completedAt,
      isFromMyPracticeCompleted = false,
      onRemove,
      ...rest
    },
    ref
  ) => {
    const insideRouter = useInRouterContext()
    const FinalclassCount = classCount || workoutCount
    const isBundle = String(rest?.actableType).toLowerCase() === 'bundle'
    const seriesUrl = url || (isBundle ? `/bundles/${slug}` : `/series/${slug}`)
    const [favoritesId, setFavoritesId] = useState(
      rest?.planSubscriptions?.[0]?.id
    )
    const filteredSubStyles = subcategories?.filter(
      (subStyle) => subStyle.slug !== 'single-classes'
    )
    const image =
      coverPhoto?.url ||
      (thumbnails && (thumbnails.medium || thumbnails.default)) ||
      (introVideoThumbnail && introVideoThumbnail) ||
      (thumbnailImage === null ? '' : thumbnailImage)

    const { setSnapShot } = useUserContext() || {}

    const removeSeries = (id) => {
      setFavoritesId(null)
      axios
        .delete(`/plan_subscriptions/${id}`)
        .then((res) => {
          if (res.status === 200) {
            setFavoritesId(null)
            favoriteCallback?.()
            setSnapShot && updateSnapShot()
          } else {
            setFavoritesId(id)
          }
        })
        .catch((error) => {
          console.error(error)
          setFavoritesId(id)
        })
    }

    const addSeries = (id) => {
      setFavoritesId(id)
      axios
        .post(`/plan_subscriptions`, { plan_id: id })
        .then((res) => {
          if (res.status === 200) {
            window.dataLayer?.push(camelToSnake(res?.data?.ga4Events))
            //series_added_my_practice event to braze
            window.braze.logCustomEvent('series_added_my_practice', {
              ...camelToSnake(res?.data?.ga4Events)
            })
            setFavoritesId(res?.data?.id || null)
            favoriteCallback?.()
            setSnapShot && updateSnapShot()
          } else {
            setFavoritesId(null)
          }
        })
        .catch((error) => {
          console.error(error)
          setFavoritesId(null)
        })
    }

    const updateSnapShot = async () => {
      try {
        const res = await axios.get('/my_practice/homepage_summary')
        if (res) {
          setSnapShot?.(res?.data)
        }
      } catch (e) {
        console.log('e', e)
      }
    }

    const pushEvent = (e) => {
      if (!eventName) {
        return
      }
      const coachNames = coaches
        ?.map((item) => `${item.firstName} ${item.lastName}`)
        .join(', ')

      const event_data = {
        event: eventName,
        position,
        instructor: coachNames,
        series: title,
        ...(location && { location })
      }
      if (insideRouter) {
        window.dataLayer ||= []
        window.dataLayer.push(event_data)
        return
      }
      storeEventInLocalStorage(event_data)
    }

    return (
      <Main
        ref={ref}
        flexDirection='column'
        alignItems={alignBox}
        position='relative'
        className={carouselItem ? 'swiper-slide unstyled' : 'unstyled'}
        {...(isBundle
          ? {
              as: 'a',
              href: seriesUrl
            }
          : {
              as: CommonLink,
              to: seriesUrl
            })}
        onClick={pushEvent}
        style={{
          minWidth: carouselItem ? '212px' : '173px'
        }}
      >
        {primaryCategory && isFevIcon && !isBundle && !Boolean(hideSave) && (
          <HeartIconBox
            zIndex={'1'}
            right={['5px', '6px', '6px', '4px']}
            top={['5px', '7px', '7px', '4px']}
            position={'absolute'}
            width={'32px'}
            height={'32px'}
            display={'flex'}
            borderRadius={'50%'}
            alignItems={'center'}
            justifyContent={'center'}
            onClick={(e) => {
              e.stopPropagation()
              e.preventDefault()
              !favoritesId ? addSeries(id) : removeSeries(favoritesId)
            }}
            backgroundColor={'#FFFFFF99'}
          >
            <ResponsiveSVG
              SVG={favoritesId ? heartFilledSVG : heartSVG}
              style={{
                cursor: 'pointer'
              }}
              width='24px'
              height='24px'
            />
          </HeartIconBox>
        )}
        {image ? (
          <Box position='relative' mb={['8px', '10px']}>
            <Image
              src={image}
              objectFit='cover'
              position='absolute'
              aspectRatio='16/9'
              top='0'
              left='0'
              style={{ aspectRatio: '16/9' }}
              className='series-image'
            />
            {FinalclassCount > 0 && (
              <ClassCountBadge
                bottom='16px !important'
                left='16px !important'
              >{`${FinalclassCount} Class${
                FinalclassCount <= 1 ? '' : 'es'
              }`}</ClassCountBadge>
            )}
            {showLabel(membershipTier, availabilityGating) && (
              <AccessIdentifier
                availabilityGating={availabilityGating}
                isGridView={isGridView}
                membershipTier={membershipTier}
                bottomPosition={'16px'}
              />
            )}
          </Box>
        ) : (
          <Box className='no-image' mb={['8px', '10px']}>
            <ResponsiveSVG
              width={['68px', '68px', '130px', '130px']}
              height={['68px', '68px', '130px', '130px']}
              SVG={AlomovesIconSVG}
              className='no-thumbnail'
            />
          </Box>
        )}
        <TitleRow>
          {title && (
            <CardTitle
              id='SeriesTitle'
              as='div'
              paddingBottom='6px'
              variant={variantTitle}
            >
              {title}
            </CardTitle>
          )}
          {isFromMyPracticeCompleted && (
            <PopoverMenu
              trigger={
                <ResponsiveSVG SVG={ThreeDots} width='24px' height='24px' />
              }
              options={[
                {
                  label: 'Remove from Completed',
                  onClick: () => {
                    onRemove()
                  }
                }
              ]}
            />
          )}
        </TitleRow>
        {instructors && (
          <CardSubtitle as='div'>
            <NameList people={instructors || coaches} />
          </CardSubtitle>
        )}
        {coaches && <CoachesListComponent coaches={coaches} />}

        <CardSubtitle as='div' marginTop='4px'>
          {difficultyLevel && `${difficultyLevel}`}
        </CardSubtitle>
        {completedAt ? (
          <P className='completed-at'>
            {isFromMyPracticeCompleted ? 'Program Completed' : 'Completed'}:{' '}
            {getLocalDate(completedAt)}
          </P>
        ) : null}
        {filteredSubStyles && (
          <SubCategoryChipList
            subcategories={filteredSubStyles}
            isGridView={isGridView}
          />
        )}
      </Main>
    )
  }
)

SeriesCard.propTypes = {
  duration: PropTypes.string,
  instructors: PropTypes.array,
  id: PropTypes.string,
  primaryCategory: PropTypes.object,
  primarySubcategory: PropTypes.object,
  title: PropTypes.string.isRequired,
  thumbnails: PropTypes.object,
  coverPhoto: PropTypes.array,
  coaches: PropTypes.array,
  alignBox: PropTypes.string,
  variantSubtitle: PropTypes.string,
  variantTitle: PropTypes.string,
  rest: PropTypes.object
}

export default SeriesCard
